import { useMemo, useState, useCallback, useEffect } from 'react'
import useQuery from '../../../helpers/JIFI/useQuery';
import { Col } from '@shadcn/components/ui/col';
import { Input } from '@shadcn/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/components/ui/select';
import { Button } from '@shadcn/components/ui/button';
import {
  createColumnHelper,
  useReactTable,
  flexRender,
  getCoreRowModel,
} from '@tanstack/react-table';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton } from '@shadcn/components/ui/table';
import { TablePagination } from '@components/supply-chain/TablePagination';
import Breadcrumbs from '@components/Common/Breadcrumb';

const columnHelper = createColumnHelper();

const IpLogListPage = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hasAuth, setHasAuth] = useState('all');
  const [errorMessage, setErrorMessage] = useState('');

  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        setErrorMessage('Start date cannot be after end date');
      } else {
        setErrorMessage('');
      }
    } else {
      setErrorMessage('');
    }
  }, [startDate, endDate]);

  const queryData = useMemo(() => {
    
    if (errorMessage) return null;
    
    const params = { page, perPage };
    if (debouncedSearchTerm) params.searchTerm = debouncedSearchTerm;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (hasAuth === 'true') params.hasAuth = 'true';
    if (hasAuth === 'false') params.hasAuth = 'false';
    return params;
  }, [page, perPage, debouncedSearchTerm, startDate, endDate, hasAuth, errorMessage]);

  const [ipLogs, ipLogState] = useQuery({
    url: `/ip-logs/list`,
    queryData: queryData || {},
    runQuery: !errorMessage && queryData !== null,
  });

  const [stats, statsState] = useQuery({
    url: `/ip-logs/stats`,
    queryData: {},
  });

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStartDate('');
    setEndDate('');
    setHasAuth('all');
    setErrorMessage('');
    setPage(1);
  }, []);

  const ipLogColumns = useMemo(
    () => [
      columnHelper.accessor('ip', {
        header: () => 'IP Address',
        cell: ({ row }) => (
          <div className="font-mono text-sm font-medium text-gray-900">
            {row.original.ip}
          </div>
        ),
      }),
      columnHelper.accessor('endpoint', {
        header: () => 'Endpoint',
        cell: ({ row }) => (
          <div className="text-sm text-gray-600 max-w-md truncate">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-2">
              {row.original.method}
            </span>
            {row.original.endpoint}
          </div>
        ),
      }),      columnHelper.accessor('geo', {
        header: () => 'Location',
        cell: ({ row }) => {
          const geo = row.original.geo;
          if (!geo?.country) return <span className="text-gray-400">Unknown</span>;
          
          return (
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {geo.country} {geo.countryCode && `(${geo.countryCode})`}
              </div>
              {geo.city && (
                <div className="text-gray-500">{geo.city}</div>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('user', {
        header: () => 'User',
        cell: ({ row }) => {
          const user = row.original.user;
          const attemptedEmail = row.original.attemptedEmail;
          
          if (!user && !attemptedEmail) return <span className="text-gray-400">Anonymous</span>;
          
          return (
            <div className="text-sm">
              {user ? (
                <>
                  <div className="font-medium text-gray-900">
                    {user.userName}
                  </div>
                  <div className="text-gray-500 text-xs">
                    {user.email}
                  </div>
                </>
              ) : (
                <div className="text-red-600 text-xs font-medium">
                  Attempted: {attemptedEmail}
                </div>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('hasAuth', {
        header: () => 'Auth Status',
        cell: ({ row }) => {
          const { hasAuth, authSuccess, attemptedEmail } = row.original;
          
          // For auth endpoints, show success/failure
          if (attemptedEmail || row.original.endpoint?.includes('/auth')) {
            if (authSuccess === true) {
              return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Login Success
                </span>
              );
            } else if (authSuccess === false) {
              return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Login Failed
                </span>
              );
            }
          }
          
          // For non-auth endpoints, show traditional auth status
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              hasAuth 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {hasAuth ? 'Authenticated' : 'Anonymous'}
            </span>
          );
        },
      }),
      columnHelper.accessor('attemptedEmail', {
        header: () => 'Login Attempt',
        cell: ({ row }) => {
          const { attemptedEmail, authSuccess } = row.original;
          
          if (!attemptedEmail) return <span className="text-gray-400">-</span>;
          
          return (
            <div className="text-sm">
              <div className={`font-medium ${authSuccess === false ? 'text-red-600' : 'text-gray-900'}`}>
                {attemptedEmail === 'google_auth_attempt' ? 'Google Auth' : attemptedEmail}
              </div>
              {authSuccess !== undefined && (
                <div className={`text-xs ${authSuccess ? 'text-green-600' : 'text-red-600'}`}>
                  {authSuccess ? 'Success' : 'Failed'}
                </div>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('count', {
        header: () => 'Requests',
        cell: ({ row }) => (
          <div className="text-sm font-medium text-gray-900">
            {row.original.count}
          </div>
        ),
      }),
      columnHelper.accessor('lastSeen', {
        header: () => 'Last Seen',
        cell: ({ row }) => (
          <div className="text-sm text-gray-500">
            {new Date(row.original.lastSeen).toLocaleString()}
          </div>
        ),
      }),
    ],
    []
  );
  const ipLogData = useMemo(() => ipLogs?.data || [], [ipLogs]);

  const ipLogTable = useReactTable({
    data: ipLogData,
    columns: ipLogColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className="page-content">
        <div className="w-full px-3 mx-auto xl:max-w-[85%] mb-6">
          <Breadcrumbs title="System" breadcrumbItem="IP Tracking" />
          
          {/* Statistics Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {statsState === 'loading' ? (
              // Loading skeleton for stats
              Array.from({ length: 5 }).map((_, index) => (
                <Card key={index}>
                  <CardBody className="p-4">
                    <div className="h-8 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  </CardBody>
                </Card>
              ))
            ) : stats ? (
              // Actual stats
              <>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-2xl font-bold text-blue-600">{stats.totalLogs?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Total Logs</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-2xl font-bold text-green-600">{stats.totalRequests?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Total Requests</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-2xl font-bold text-purple-600">{stats.uniqueIPs?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Unique IPs</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-2xl font-bold text-emerald-600">{stats.authenticatedRequests?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Authenticated</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-2xl font-bold text-orange-600">{stats.unauthenticatedRequests?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Anonymous</div>
                  </CardBody>
                </Card>
              </>
            ) : null}
          </div>

          <Col className="w-full">
            <Card className="mb-10 shadow-lg">
              <CardBody className="pt-4">
                {/* Advanced Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                    <Input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="h-9 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="IP, endpoint, user..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className={`h-9 focus:ring-blue-500 ${errorMessage ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={`h-9 focus:ring-blue-500 ${errorMessage ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Authentication</label>
                    <Select value={hasAuth} onValueChange={setHasAuth}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All requests" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All requests</SelectItem>
                        <SelectItem value="true">Authenticated</SelectItem>
                        <SelectItem value="false">Anonymous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Error Message */}
                {errorMessage && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800 font-medium">
                          {errorMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between items-center mb-4">
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="text-sm"
                  >
                    Clear Filters
                  </Button>
                  <div className="text-sm text-gray-500">
                    {errorMessage ? 'Please fix errors above' : ipLogState === 'loading' ? 'Loading...' : `${ipLogs?.total || 0} total records`}
                  </div>
                </div>

                {/* Table section */}
                <div className="table-responsive rounded-lg border border-gray-200">
                  <Table className="table-auto w-full">
                    <TableHeader>
                      {ipLogTable.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id} className="bg-gray-50">
                          {headerGroup.headers.map((header) => (
                            <TableHead key={header.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    {errorMessage ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={8} className="px-6 py-4 text-center text-gray-500">
                            Please fix the errors above to load data
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : ipLogState === 'loading' ? (
                      <TableSkeleton columns={8} perPage={perPage} />
                    ) : (
                      <TableBody>
                        {ipLogTable.getRowModel().rows?.length ? (
                          ipLogTable.getRowModel().rows.map((row) => (
                            <TableRow key={row.id} className="hover:bg-gray-50">
                              {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id} className="px-6 py-4 whitespace-nowrap">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} className="px-6 py-4 text-center text-gray-500">
                              No IP logs found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}
                  </Table>
                </div>

                {/* Pagination */}
                {!errorMessage && ipLogState !== 'loading' && ipLogs?.totalPages > 1 && (
                  <div className="mt-4">
                    <TablePagination
                      page={page}
                      count={ipLogs.total}
                      perPage={perPage}
                      setPage={setPage}
                      setPerPage={setPerPage}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </div>
      </div>
    </>
  );
};

export default IpLogListPage;
